// src/components/invitations/useMyInvites.js
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { collection, deleteDoc, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase-client';
import { useOnlineRefreshSignal } from '../../utils/onlineStatus';

// Custom hook to fetch & reload invitations stored under invitations/{userId}/invites
export function useMyInvites() {
  const {
    user
  } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const onlineRefreshSignal = useOnlineRefreshSignal();
  const reloadInvites = useCallback(async () => {
    if (!user?.uid) {
      setInvites([]);
      setLoading(false);
      return;
    }
    const userInvitesCol = collection(db, 'invitations', user.uid, 'invites');
    setLoading(true);
    try {
      const [snap, userSnap] = await Promise.all([getDocs(userInvitesCol), getDoc(doc(db, 'users', user.uid))]);
      const profile = userSnap.exists() ? userSnap.data() || {} : {};
      const teamRoles = profile?.team_roles || {};
      const familyRoles = profile?.family_roles || {};
      const docs = snap.docs.map(inviteDoc => ({
        id: inviteDoc.id,
        ...inviteDoc.data()
      }));
      const staleInvites = docs.filter(invite => {
        if (invite?.inviteType === 'Team Join Request') {
          const teamId = typeof invite?.team === 'string' ? invite.team.trim() : '';
          return Boolean(teamId && teamRoles?.[teamId]);
        }
        if (invite?.inviteType === 'Family') {
          const familyId = typeof invite?.familyId === 'string' ? invite.familyId.trim() : '';
          return Boolean(familyId && familyRoles?.[familyId]);
        }
        return false;
      });
      if (staleInvites.length > 0) {
        await Promise.all(staleInvites.map(invite => deleteDoc(doc(db, 'invitations', user.uid, 'invites', invite.id)).catch(() => {})));
      }
      const staleIds = new Set(staleInvites.map(invite => invite.id));
      setInvites(docs.filter(invite => !staleIds.has(invite.id)));
    } catch (err) {
      // Keep the last successful invitation list until the online refresh retries.
    } finally {
      setLoading(false);
    }
  }, [user]);
  useEffect(() => {
    reloadInvites();
  }, [onlineRefreshSignal, reloadInvites]);
  return {
    invites,
    loading,
    reloadInvites
  };
}
